/**
 * The small marks every findings surface shares: the Linear-style severity
 * bars, the status circle, the basis icon, the property chip, a person's
 * face, and the hover card that holds a truncated line's full text. Colours
 * come from the product's finding tones and tokens.
 *
 * @module components/regulation/finding/findingGlyphs
 */
import { FileQuestionIcon, LandmarkIcon, ShieldIcon } from "lucide-react";
import type { ReactElement, ReactNode } from "react";

import { cn } from "~/lib/utils";
import type { OrganizationPerson } from "../../../state/governance";
import { Avatar, AvatarFallback, AvatarImage, personInitials } from "../../ui/avatar";
import { HoverCard, HoverCardPopup, HoverCardTrigger } from "../../ui/hover-card";
import { Tip } from "../../ui/tip";
import { SEVERITY_LABEL, type FindingBasisKind, type FindingChip, type FindingItemStatus, type Severity } from "./findingItem";

const SEVERITY_INK: Readonly<Record<Severity, string>> = {
  critical: "text-red-600 dark:text-red-400",
  high: "text-orange-500 dark:text-orange-400",
  medium: "text-amber-500 dark:text-amber-300",
  low: "text-sky-500 dark:text-sky-400",
};

/**
 * Priority as Linear draws it: a filled square for critical, bars below it, dashes when unrated.
 * `quiet` keeps red for critical only and draws the rest in 72% ink, for surfaces where colour means "act now".
 */
export function SeverityGlyph({
  severity,
  tone = "severity",
  className,
}: {
  readonly severity: Severity | null;
  readonly tone?: "severity" | "quiet";
  readonly className?: string;
}) {
  const label = severity ? `${SEVERITY_LABEL[severity]} severity` : "No severity";
  const filled = severity === "high" ? 3 : severity === "medium" ? 2 : severity === "low" ? 1 : 0;
  return (
    <Tip label={label}>
      <span
        role="img"
        aria-label={label}
        className={cn(
          "inline-flex size-4 shrink-0 items-center justify-center",
          tone === "quiet" && severity !== "critical"
            ? "text-foreground/72"
            : severity
              ? SEVERITY_INK[severity]
              : "text-muted-foreground/60",
          className,
        )}
      >
        {severity === "critical" ? (
          <svg viewBox="0 0 16 16" className="size-3.5" aria-hidden>
            <rect x="1" y="1" width="14" height="14" rx="3.5" fill="currentColor" />
            <rect x="7.1" y="3.8" width="1.8" height="5.6" rx="0.9" fill="white" />
            <circle cx="8" cy="11.6" r="1.05" fill="white" />
          </svg>
        ) : (
          <svg viewBox="0 0 16 16" className="size-3.5" aria-hidden>
            {[0, 1, 2].map((bar) => (
              <rect
                key={bar}
                x={2 + bar * 4.5}
                y={severity ? 10 - bar * 3.5 : 7}
                width="3"
                height={severity ? 4 + bar * 3.5 : 1.6}
                rx="0.8"
                fill="currentColor"
                opacity={severity === null || bar < filled ? 1 : 0.22}
              />
            ))}
          </svg>
        )}
      </span>
    </Tip>
  );
}

const STATUS_LABEL: Readonly<Record<FindingItemStatus, string>> = {
  open: "Open",
  resolved: "Resolved",
  dismissed: "Dismissed",
};

/** Linear's status circle: open (ring), resolved (check), dismissed (cross). */
export function StatusIcon({ status, className }: { readonly status: FindingItemStatus; readonly className?: string }) {
  return (
    <Tip label={STATUS_LABEL[status]}>
      <span role="img" aria-label={STATUS_LABEL[status]} className={cn("inline-flex size-4 shrink-0 items-center justify-center", className)}>
        <svg viewBox="0 0 16 16" className="size-3.5" aria-hidden>
          {status === "open" ? (
            <circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-orange-500 dark:text-orange-400" />
          ) : status === "resolved" ? (
            <>
              <circle cx="8" cy="8" r="7" className="fill-emerald-600 dark:fill-emerald-500" />
              <path d="m5 8.2 2 2 4-4.2" fill="none" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </>
          ) : (
            <>
              <circle cx="8" cy="8" r="7" className="fill-muted-foreground/45" />
              <path d="m5.6 5.6 4.8 4.8m0-4.8-4.8 4.8" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
            </>
          )}
        </svg>
      </span>
    </Tip>
  );
}

export const findingStatusLabel = (status: FindingItemStatus) => STATUS_LABEL[status];

export function BasisIcon({ kind, className }: { readonly kind: FindingBasisKind; readonly className?: string }) {
  const Icon = kind === "regulation" ? LandmarkIcon : kind === "control" ? ShieldIcon : FileQuestionIcon;
  return <Icon aria-hidden className={cn("size-3.5 shrink-0", className)} />;
}

const CHIP_TONE: Readonly<Record<FindingChip["kind"], string>> = {
  "needs-recheck": "bg-amber-400/15 text-amber-800 dark:text-amber-200",
  "in-progress": "bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary-foreground/90",
};

/** The one state worth a word on an open row. */
export function StateChip({ chip, className }: { readonly chip: FindingChip; readonly className?: string }) {
  return (
    <Tip label={chip.detail}>
      <span
        data-state-chip={chip.kind}
        className={cn(
          "inline-flex w-fit items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium",
          CHIP_TONE[chip.kind],
          className,
        )}
      >
        {chip.label}
      </span>
    </Tip>
  );
}

const PROPERTY_CHIP =
  "inline-flex h-5 min-w-0 shrink-0 items-center gap-1 rounded-full border border-border/80 px-1.5 text-[11px] text-muted-foreground [&_svg]:size-3 [&_svg]:shrink-0";

/**
 * Linear's property chip: hairline border, tiny icon, 11px text.
 *
 * `expand` is for a width-capped chip on a list row: hovering it lays the whole
 * pill (its `title`, or its text) over the row leftwards from the chip's right
 * edge instead of a tooltip, so nothing reflows. On a row with a stretched link
 * the caller lifts the chip above it (see `FindingRow`).
 */
export function PropertyChip({
  icon,
  children,
  className,
  title,
  expand = false,
}: {
  readonly icon?: ReactNode;
  readonly children: ReactNode;
  readonly className?: string | undefined;
  readonly title?: string | undefined;
  readonly expand?: boolean;
}) {
  if (expand) {
    return (
      <span
        data-property-chip-expand=""
        className={cn("group/chip relative inline-flex", className, "max-w-none")}
      >
        <span className={cn(PROPERTY_CHIP, className)}>
          {icon}
          <span className="truncate">{children}</span>
        </span>
        {/* Starts exactly over the chip and grows its max-width, so the text unrolls leftwards. */}
        <span
          aria-hidden
          className={cn(
            PROPERTY_CHIP,
            "pointer-events-none absolute end-0 top-0 max-w-full overflow-hidden whitespace-nowrap bg-card opacity-0 [transition:max-width_320ms_cubic-bezier(0.22,1,0.36,1),opacity_140ms_ease-out,color_320ms_ease-out] group-hover/chip:max-w-[36rem] group-hover/chip:text-foreground group-hover/chip:opacity-100 group-hover/chip:shadow-sm motion-reduce:transition-none",
          )}
        >
          {icon}
          <span className="truncate">{title ?? children}</span>
        </span>
      </span>
    );
  }
  return (
    <Tip label={title}>
      <span className={cn(PROPERTY_CHIP, className)}>
        {icon}
        <span className="truncate">{children}</span>
      </span>
    </Tip>
  );
}

/** A person's face, small: the workspace avatar, initials when there is no picture. */
export function PersonFace({ person, className }: { readonly person: OrganizationPerson; readonly className?: string }) {
  return (
    <Avatar className={cn("size-5 text-[9px]", className)}>
      {person.avatarUrl ? <AvatarImage src={person.avatarUrl} /> : <AvatarFallback>{personInitials(person)}</AvatarFallback>}
    </Avatar>
  );
}

export const personLabel = (person: Pick<OrganizationPerson, "name" | "email">) => person.name?.trim() || person.email;

/** A hover card with the full text behind a truncated label. */
export function Hint({
  content,
  children,
  render,
  side = "bottom",
  align = "start",
  className,
}: {
  readonly content: ReactNode;
  readonly children: ReactNode;
  readonly render: ReactElement;
  readonly side?: "top" | "bottom";
  readonly align?: "start" | "center" | "end";
  readonly className?: string;
}) {
  return (
    <HoverCard>
      <HoverCardTrigger delay={200} closeDelay={80} render={render}>
        {children}
      </HoverCardTrigger>
      <HoverCardPopup side={side} align={align} className={cn("w-80 px-3.5 py-3 text-xs leading-5 text-muted-foreground", className)}>
        {content}
      </HoverCardPopup>
    </HoverCard>
  );
}

/** What a member sees where an admin's decisions would be. */
export function WaitingForAdmin({ what = "resolve or dismiss findings" }: { readonly what?: string }) {
  return (
    <p className="flex items-center gap-2 rounded-lg border border-dashed border-border px-3 py-2 text-xs text-muted-foreground">
      <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-amber-500" />
      <span>
        <span className="font-medium text-foreground">Waiting for an admin.</span> Only admins can {what}; you can still
        comment.
      </span>
    </p>
  );
}
